import { defineStore } from 'pinia';
import { api } from '@/utils/api';

export const useFileStore = defineStore('files', {
    state: () => ({
        files: null,
        entityTypes: [],
        storageUsage: null,
        isLoading: false,
        error: null,
    }),

    getters: {
        entityTypeMap(state) {
            return Object.fromEntries(state.entityTypes.map(t => [t.value, t.displayName]));
        }
    },

    actions: {
        async fetchStorageUsage(companyId) {
            try {
                const response = await api.get(`/companies/${companyId}/files/storage-usage`);
                console.debug('Storage usage fetched:', response.data);
                this.storageUsage = response.data;
            } catch (error) {
                console.error('Error fetching storage usage:', error);
            }
        },
        async fetchEntityTypes(companyId) {
            try {
                const response = await api.get(`/companies/${companyId}/files/entity-types`);
                this.entityTypes = response.data;
            } catch (error) {
                console.error('Error fetching entity types:', error);
            }
        },
        async fetchFiles(companyId, page = 0, size = 10, filter = {}) {
            this.isLoading = true;
            try {
                const params = { page, size };
                if (filter.createdFrom) params.createdFrom = filter.createdFrom;
                if (filter.createdTo) params.createdTo = filter.createdTo;
                if (filter.projectId) params.projectId = filter.projectId;
                if (filter.entityType) params.entityType = filter.entityType;
                if (filter.uploadedById) params.uploadedById = filter.uploadedById;
                const response = await api.get(`/companies/${companyId}/files`, {
                    params
                });
                console.debug('Files fetched:', response.data);
                this.files = response.data;
            } catch (error) {
                console.error('Error fetching files:', error);
            } finally {
                this.isLoading = false;
            }
        },

        async uploadFile(companyId, file, projectId = null) {
            this.isLoading = true;
            try {
                const formData = new FormData();
                formData.append('file', file);
                if (projectId) formData.append('projectId', projectId);
                const response = await api.post(`/companies/${companyId}/files/upload`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                console.log('File uploaded:', response.data);
                if (this.files && this.files.content) {
                    this.files.content.unshift(response.data);
                }
                return response.data;
            } catch (error) {
                console.error('Error uploading file:', error);
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async fetchFilesByIds(companyId, ids) {
            try {
                const response = await api.get(`/companies/${companyId}/files/by-ids`, {
                    params: { ids: ids.join(',') },
                    skipErrorNotification: true,
                });
                return response.data;
            } catch (error) {
                console.error('Error fetching files by ids:', error);
                throw error;
            }
        },

        async searchFiles(companyId, text, options = {}) {
            try {
                const params = { text };
                if (options.companyMemberId) params.companyMemberId = options.companyMemberId;
                if (options.amount) params.amount = options.amount;
                if (options.scoreThreshold !== undefined) params.scoreThreshold = options.scoreThreshold;
                const response = await api.get(`/files/${companyId}/search`, { params, skipErrorNotification: true });
                return response.data;
            } catch (error) {
                console.error('Error searching files:', error);
                throw error;
            }
        },

        async deleteFile(companyId, fileId) {
            try {
                await api.delete(`/companies/${companyId}/files/${fileId}`);
                if (this.files && this.files.content) {
                    this.files.content = this.files.content.filter(f => f.id !== fileId);
                }
            } catch (error) {
                console.error('Error deleting file:', error);
                throw error;
            }
        },
    },
});
